import { useState, useEffect, useCallback } from 'react';

import { useRouter, usePathname, useSearchParams } from '@/routes/hooks';

import { CONFIG } from '@/config-global';

import { SplashScreen } from '@/components/SplashScreen';

import { useAuthContext } from '@/hooks/use-auth-context';

// ----------------------------------------------------------------------

type Props = {
  children: React.ReactNode;
};

export function AuthGuard({ children }: Props) {
  const router = useRouter();

  const pathname = usePathname();

  const searchParams = useSearchParams();

  const { authenticated, loading, user } = useAuthContext();

  const [isChecking, setIsChecking] = useState<boolean>(true);

  const createQueryString = useCallback(
    (name: string, value: string) => {
      const params = new URLSearchParams(searchParams.toString());
      params.set(name, value);

      return params.toString();
    },
    [searchParams]
  );

  const checkPermissions = async (): Promise<void> => {
    if (loading) {
      return;
    }

    if (!authenticated) {
      const { method } = CONFIG.auth;

      const signInPath = {
        supabase: '/login',
      }[method];

      const href = `${signInPath}?${createQueryString('returnTo', pathname)}`;

      router.replace(href);
      return;
    }

    if (user?.user_metadata?.needsPasswordSetup === true) {
      router.replace('/set-password');
      return;
    }
    setIsChecking(false);
  };

  useEffect(() => {
    checkPermissions();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [authenticated, loading, user?.user_metadata?.needsPasswordSetup]);

  if (isChecking || user?.user_metadata?.needsPasswordSetup === true) {
    return <SplashScreen />;
  }

  return <>{children}</>;
}
